// Week and month views of the appointment book (Phase 1 quotation G6): how
// full each day is, and how many were seen, missed or cancelled. Read-only —
// clicking a day opens it in the day view, where booking happens.
import { useQuery } from '@tanstack/react-query';
import { keys } from '@/lib/queryKeys';
import { LoadingState, ErrorState } from '@/components/ui/States';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/cn';
import { fetchCalendar } from './api';
import type { CalendarDayCount } from './types';

const ymd = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const parse = (s: string) => { const [y, m, d] = s.split('-').map(Number); return new Date(y!, (m ?? 1) - 1, d ?? 1); };
const addDays = (d: Date, n: number) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/** Monday of the week containing `d`. */
const weekStart = (d: Date) => addDays(d, -((d.getDay() + 6) % 7));

type Totals = Omit<CalendarDayCount, 'date' | 'doctor_id' | 'doctor_name'>;
const sum = (rows: CalendarDayCount[]): Totals => rows.reduce((t, r) => ({
  open: t.open + r.open, booked: t.booked + r.booked, completed: t.completed + r.completed,
  no_show: t.no_show + r.no_show, cancelled: t.cancelled + r.cancelled,
}), { open: 0, booked: 0, completed: 0, no_show: 0, cancelled: 0 });

export function AppointmentsCalendar({ mode, anchor, doctorId, today, onAnchorChange, onOpenDay }: {
  mode: 'week' | 'month';
  /** Any date inside the week/month shown (YYYY-MM-DD). */
  anchor: string;
  /** '' for all doctors. */
  doctorId: string;
  today: string;
  onAnchorChange: (date: string) => void;
  onOpenDay: (date: string) => void;
}) {
  const a = parse(anchor);
  const first = mode === 'week' ? weekStart(a) : weekStart(new Date(a.getFullYear(), a.getMonth(), 1));
  const dayCount = mode === 'week' ? 7 : 42;
  const from = ymd(first);
  const to = ymd(addDays(first, dayCount - 1));
  const q = useQuery({
    queryKey: keys.appointments.slots({ calendar: true, from, to, doctorId }),
    queryFn: () => fetchCalendar(from, to, doctorId || undefined),
  });
  const byDay = new Map<string, CalendarDayCount[]>();
  for (const r of q.data ?? []) byDay.set(r.date, [...(byDay.get(r.date) ?? []), r]);

  const step = (dir: 1 | -1) => onAnchorChange(ymd(mode === 'week' ? addDays(a, 7 * dir) : new Date(a.getFullYear(), a.getMonth() + dir, 1)));
  const title = mode === 'week'
    ? `${parse(from).getDate()} ${MONTHS[parse(from).getMonth()]!.slice(0, 3)} – ${parse(to).getDate()} ${MONTHS[parse(to).getMonth()]!.slice(0, 3)} ${parse(to).getFullYear()}`
    : `${MONTHS[a.getMonth()]} ${a.getFullYear()}`;

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Button size="sm" variant="ghost" onClick={() => step(-1)} aria-label="Previous">‹</Button>
        <div className="min-w-[180px] text-center text-[14px] font-bold">{title}</div>
        <Button size="sm" variant="ghost" onClick={() => step(1)} aria-label="Next">›</Button>
        <Button size="sm" variant="ghost" onClick={() => onAnchorChange(today)}>Today</Button>
        <span className="ml-auto flex flex-wrap gap-3 text-[11px] text-ink-muted">
          <Legend className="bg-info" label="Booked" /><Legend className="bg-success" label="Seen" />
          <Legend className="bg-danger" label="No-show" /><Legend className="bg-muted-foreground" label="Cancelled" />
        </span>
      </div>
      {q.isLoading ? <LoadingState /> : q.isError ? <ErrorState message="Could not load the calendar" onRetry={() => q.refetch()} /> : (
        <div className={cn('grid gap-1.5', 'grid-cols-7')}>
          {WEEKDAYS.map((w) => <div key={w} className="px-1 text-[11px] font-semibold uppercase text-ink-muted">{w}</div>)}
          {Array.from({ length: dayCount }, (_, i) => {
            const d = addDays(first, i);
            const key = ymd(d);
            const rows = byDay.get(key) ?? [];
            const t = sum(rows);
            const outside = mode === 'month' && d.getMonth() !== a.getMonth();
            return (
              <button key={key} type="button" onClick={() => onOpenDay(key)}
                className={cn('flex min-h-[86px] flex-col gap-1 rounded-[var(--radius)] border border-line bg-card p-1.5 text-left hover:border-teal',
                  outside && 'opacity-45', key === today && 'border-teal ring-1 ring-teal/40', mode === 'week' && 'min-h-[150px]')}>
                <span className={cn('text-[12px] font-semibold', key === today ? 'text-teal' : 'text-ink')}>{d.getDate()}</span>
                {t.booked + t.completed + t.no_show + t.cancelled === 0
                  ? <span className="text-[10.5px] text-ink-muted">{t.open ? `${t.open} free` : ''}</span>
                  : (
                    <span className="flex flex-wrap gap-1">
                      {t.booked > 0 && <Count className="bg-info-soft text-info-ink" n={t.booked} label="booked" />}
                      {t.completed > 0 && <Count className="bg-success-soft text-success-ink" n={t.completed} label="seen" />}
                      {t.no_show > 0 && <Count className="bg-danger-soft text-danger-ink" n={t.no_show} label="no-show" />}
                      {t.cancelled > 0 && <Count className="bg-muted text-muted-foreground" n={t.cancelled} label="cancelled" />}
                    </span>
                  )}
                {mode === 'week' && !doctorId && rows.length > 0 && (
                  <span className="mt-auto flex flex-col gap-0.5 text-[10.5px] text-ink-muted">
                    {rows.map((r) => <span key={r.doctor_id} className="truncate">{r.doctor_name}: {r.booked + r.completed + r.no_show}</span>)}
                  </span>
                )}
                {t.open > 0 && t.booked + t.completed + t.no_show + t.cancelled > 0 && <span className="mt-auto text-[10.5px] text-ink-muted">{t.open} free</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Count({ n, label, className }: { n: number; label: string; className: string }) {
  return <span className={cn('rounded px-1 text-[10.5px] font-semibold', className)} title={`${n} ${label}`}>{n}</span>;
}
function Legend({ className, label }: { className: string; label: string }) {
  return <span className="flex items-center gap-1"><span className={cn('h-2 w-2 rounded-full', className)} />{label}</span>;
}
